/**
 * Local-first persistence in IndexedDB (AGENT.md §13). Repositories never
 * overwrite silently: project saves check `revision` for conflicts.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { AppError, errorMessage } from "../core/errors";
import { parsePreset, parseProject, parseSetlist } from "./serialize";
import type { AssetRecord, Preset, Project, Setlist } from "./schema";
import type { PracticeAttempt } from "../learning/stats";

export const DB_NAME = "music-platform";
export const DB_VERSION = 3;

interface PlatformDB extends DBSchema {
  projects: { key: string; value: Project; indexes: { updatedAt: string } };
  presets: { key: string; value: Preset; indexes: { kind: string } };
  assets: { key: string; value: AssetRecord };
  meta: { key: string; value: unknown };
  practice: { key: string; value: PracticeAttempt; indexes: { exercise: string } };
  setlists: { key: string; value: Setlist };
}

export interface ProjectSummary {
  id: string; name: string; updatedAt: string; revision: number;
  title: string; artist: string; key: Project["song"]["key"]; transposeSemitones: number; tempoBpm: number;
}

export class LocalStore {
  private constructor(private db: IDBPDatabase<PlatformDB>) {}

  static async open(name = DB_NAME): Promise<LocalStore> {
    try {
      const db = await openDB<PlatformDB>(name, DB_VERSION, {
        // Upgrades are additive only — never delete stores holding user data.
        upgrade(db, oldVersion) {
          if (oldVersion < 1) {
            db.createObjectStore("projects", { keyPath: "id" }).createIndex("updatedAt", "updatedAt");
            db.createObjectStore("presets", { keyPath: "id" }).createIndex("kind", "instrument.kind");
            db.createObjectStore("assets", { keyPath: "id" });
            db.createObjectStore("meta");
          }
          if (oldVersion < 2) {
            db.createObjectStore("practice", { keyPath: "id" }).createIndex("exercise", "exercise");
          }
          if (oldVersion < 3) {
            db.createObjectStore("setlists", { keyPath: "id" });
          }
        },
      });
      return new LocalStore(db);
    } catch (err) {
      throw new AppError("STORAGE_ERROR", `Local storage is unavailable: ${errorMessage(err)}`, { cause: err });
    }
  }

  // --- Projects -------------------------------------------------------------

  async listProjects(): Promise<ProjectSummary[]> {
    const all = await this.db.getAll("projects");
    return all.map((p) => ({
      id: p.id, name: p.name, updatedAt: p.updatedAt, revision: p.revision,
      // Older records may predate some fields; summaries must not throw.
      title: p.song?.title ?? "", artist: p.song?.artist ?? "", key: p.song?.key ?? { tonic: "C", mode: "major" },
      transposeSemitones: p.song?.transposeSemitones ?? 0, tempoBpm: p.song?.tempoBpm ?? 120,
    }))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  /** Load and re-validate (stored data may predate a schema change). */
  async loadProject(id: string): Promise<Project> {
    const p = await this.db.get("projects", id);
    if (!p) throw new AppError("NOT_FOUND", "Project not found.");
    return parseProject(p);
  }

  /**
   * Save with optimistic concurrency. `project.revision` must equal the stored
   * revision (or the project must be new). Returns the saved copy (revision+1).
   */
  async saveProject(project: Project, now = new Date()): Promise<Project> {
    const tx = this.db.transaction("projects", "readwrite");
    const existing = await tx.store.get(project.id);
    if (existing && existing.revision !== project.revision) {
      tx.abort();
      await tx.done.catch(() => undefined);
      throw new AppError("PROJECT_CONFLICT", `"${existing.name}" was changed elsewhere (e.g. another tab). Save as a copy or reload it first.`);
    }
    const saved: Project = { ...project, revision: project.revision + 1, updatedAt: now.toISOString() };
    parseProject(saved); // never persist an invalid document
    await tx.store.put(saved);
    await tx.done;
    return saved;
  }

  async deleteProject(id: string): Promise<void> {
    await this.db.delete("projects", id);
  }

  async hasProject(id: string): Promise<boolean> {
    return (await this.db.getKey("projects", id)) !== undefined;
  }

  // --- Presets --------------------------------------------------------------

  async listPresets(kind?: Preset["instrument"]["kind"]): Promise<Preset[]> {
    const all = kind ? await this.db.getAllFromIndex("presets", "kind", kind) : await this.db.getAll("presets");
    return all.sort((a, b) => a.name.localeCompare(b.name));
  }

  async savePreset(preset: Preset): Promise<Preset> {
    const valid = parsePreset(preset);
    await this.db.put("presets", valid);
    return valid;
  }

  async deletePreset(id: string): Promise<void> {
    await this.db.delete("presets", id);
  }

  // --- Assets ---------------------------------------------------------------

  async putAsset(asset: AssetRecord): Promise<void> {
    await this.db.put("assets", asset);
  }

  async getAsset(id: string): Promise<AssetRecord | undefined> {
    return this.db.get("assets", id);
  }

  async listAssets(): Promise<Omit<AssetRecord, "blob">[]> {
    return (await this.db.getAll("assets")).map(({ blob: _blob, ...meta }) => meta);
  }

  // --- Setlists ------------------------------------------------------------

  async listSetlists(): Promise<Setlist[]> {
    const all = await this.db.getAll("setlists");
    const valid: Setlist[] = [];
    for (const s of all) { try { valid.push(parseSetlist(s)); } catch { /* skip corrupt records rather than failing the list */ } }
    return valid.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "") || b.updatedAt.localeCompare(a.updatedAt));
  }

  async loadSetlist(id: string): Promise<Setlist> {
    const s = await this.db.get("setlists", id);
    if (!s) throw new AppError("NOT_FOUND", "Setlist not found.");
    return parseSetlist(s);
  }

  /** Save with the same optimistic-concurrency rule as projects. */
  async saveSetlist(setlist: Setlist, now = new Date()): Promise<Setlist> {
    const tx = this.db.transaction("setlists", "readwrite");
    const existing = await tx.store.get(setlist.id);
    if (existing && existing.revision !== setlist.revision) {
      tx.abort();
      await tx.done.catch(() => undefined);
      throw new AppError("PROJECT_CONFLICT", `Setlist "${existing.name}" was changed elsewhere. Reload it before saving.`);
    }
    const saved = parseSetlist({ ...setlist, revision: setlist.revision + 1, updatedAt: now.toISOString() });
    await tx.store.put(saved);
    await tx.done;
    return saved;
  }

  async deleteSetlist(id: string): Promise<void> {
    await this.db.delete("setlists", id);
  }

  // --- Practice (Learning Lab) ---------------------------------------------

  async addAttempt(a: PracticeAttempt): Promise<void> {
    await this.db.put("practice", a);
  }

  async listAttempts(exercise: PracticeAttempt["exercise"]): Promise<PracticeAttempt[]> {
    return this.db.getAllFromIndex("practice", "exercise", exercise);
  }

  // --- Meta (small per-browser settings and progress state) -------------------

  async getMeta<T>(key: string): Promise<T | undefined> {
    return (await this.db.get("meta", key)) as T | undefined;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await this.db.put("meta", value, key);
  }

  close(): void {
    this.db.close();
  }
}

/** Ask the browser not to evict local data. Returns the resulting state. */
export async function requestPersistentStorage(): Promise<"granted" | "denied" | "unsupported"> {
  const s = globalThis.navigator?.storage;
  if (!s?.persist) return "unsupported";
  try {
    if (await s.persisted()) return "granted";
    return (await s.persist()) ? "granted" : "denied";
  } catch {
    return "denied";
  }
}
